import { useEffect, useState } from "react";
import clsx from "clsx";
import { InputBase } from "@material-ui/core";
import { Cell, ICell } from "./";

interface InputCellProps extends ICell {
  value: any;
  disableInput: boolean;
  onChange?: (rowIndex: number | undefined, dataKey: string, value) => any;
  onEnableInput?: (rowIndex: number | undefined, dataKey: string) => any;
  onChangeInputValue: (
    rowIndex: number | undefined,
    dataKey: string,
    value
  ) => void;
}

const movimentsKeys = (rowIndex = 0, columnIndex = 0) => {
  return {
    Tab: (isShiftPressed) =>
      move(
        rowIndex,
        isShiftPressed ? columnIndex - 1 : columnIndex + 1,
        isShiftPressed ? "left" : "right"
      ),
    ArrowDown: () => move(rowIndex + 1, columnIndex, "down"),
    ArrowUp: () => move(rowIndex - 1, columnIndex, "up"),
  };
};

export const InputCell = ({
  value,
  dataKey,
  onChange,
  onChangeInputValue,
  onEnableInput,
  className,
  disableInput,
  ...props
}: InputCellProps) => {
  const { rowIndex, columnIndex } = props;
  const type = props.columnData?.inputType || "text";
  const moviments = movimentsKeys(rowIndex, columnIndex);
  const [isInput, setIsInput] = useState(false);
  const [inputValue, setInputValue] = useState(getInputValue(value));
  const [textValue, setTextValue] = useState(value);
  const [changeValue, setChangeValue] = useState(false);

  useEffect(() => {
    if (value !== textValue) setTextValue(value);
    setInputValue(getInputValue(value));
    if (isInput) setChangeValue(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInput, value]);

  const handleClickCell = (ev) => {
    ev.stopPropagation();
    if (disableInput) return;

    setIsInput(true);
    if (onEnableInput) onEnableInput(rowIndex, dataKey);
  };

  const handleChangeInput = (ev) => {
    const { value } = ev.target;
    setInputValue(value);
    if (!changeValue) setChangeValue(true);
  };

  const handleBlurInput = (ev) => {
    if (!ev) return;

    ev.stopPropagation();
    setIsInput(false);

    const { value } = ev.target;
    if (changeValue) {
      setTextValue(value);
      onChangeInputValue(rowIndex, dataKey, value);
    }
  };

  const handleKeyDownInput = (ev) => {
    ev.stopPropagation();

    if (moviments[ev.key]) {
      ev.preventDefault();
      moviments[ev.key](ev.shiftKey);
    }

    // Confirma a alteração
    if (ev.key === "Enter") {
      ev.preventDefault();
      handleBlurInput(ev);
    }
  };

  function getInputValue(value) {
    return onChange ? onChange(rowIndex, dataKey, value) : value;
  }

  return (
    <Cell
      {...props}
      className={clsx("MuiVirtualizedTable-column-input", className)}
      dataKey={dataKey}
      onClick={handleClickCell}
    >
      {isInput ? (
        <InputBase
          autoFocus={true}
          className="MuiVirtualizedTable-cell-input focused"
          name={`input-${dataKey}-${columnIndex}`}
          type={type}
          value={inputValue}
          fullWidth
          onChange={handleChangeInput}
          onBlur={handleBlurInput}
          onKeyDown={handleKeyDownInput}
          disabled={disableInput}
        />
      ) : (
        textValue
      )}
    </Cell>
  );
};

function move(rowIndex, columnIndex, direction) {
  let el: any = getElement(rowIndex, columnIndex);

  if (!el) {
    if (direction === "right") {
      el = getFirstInputCell(rowIndex + 1);
    } else if (direction === "left" && rowIndex > 0) {
      el = getLastInputCell(rowIndex - 1);
    }
  }

  if (el) el.click();
}

function getElement(rowIndex, columnIndex) {
  const element = document.querySelector(
    `.MuiVirtualizedTable-row-${rowIndex} .MuiVirtualizedTable-column-input:nth-child(${
      columnIndex + 1
    })`
  );
  return cellIsValid(element) ? element : null;
}

function getFirstInputCell(rowIndex) {
  const elements = getAllRowElements(rowIndex);
  return Array.from(elements).find((el) => cellIsValid(el));
}

function getLastInputCell(rowIndex) {
  const elements = getAllRowElements(rowIndex);
  return elements ? elements[elements.length - 1] : undefined;
}

function getAllRowElements(rowIndex) {
  return document.querySelectorAll(
    `.MuiVirtualizedTable-row-${rowIndex} .MuiVirtualizedTable-column-input`
  );
}

function cellIsValid(el: Element | null) {
  return !el?.querySelector("input");
}

export default InputCell;
